import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { getRequestActivity, subscribeRequestActivity } from "../lib/requestActivity";
import { BrandedLoaderPanel } from "./BrandedLoader";
import { ActiveLoaderBrandingContext, LoaderBrandingContext } from "../context/LoaderBrandingContext";

/** Keep the application mounted: selection, countdowns and retry refs continue. */
export default function GlobalRequestLoader({ children }) {
  const count = useSyncExternalStore(subscribeRequestActivity, getRequestActivity, () => 0);
  // A focus-triggered read may begin between pointerdown and click. Keep that
  // one gesture intact before the overlay makes the application inert.
  const [pointerActive, setPointerActive] = useState(false);
  const pointerGesture = useRef(false);
  const activity = useRef(count); activity.current = count;
  useEffect(() => {
    let release;
    const down = event => {
      if (activity.current > 0 || event.isPrimary === false) return;
      clearTimeout(release); pointerGesture.current = true; setPointerActive(true);
    };
    const up = () => {
      clearTimeout(release);
      // The native click follows pointerup in the same task.
      release = setTimeout(() => { pointerGesture.current = false; setPointerActive(false); }, 0);
    };
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("pointerup", up, true);
    document.addEventListener("pointercancel", up, true);
    window.addEventListener("blur", up);
    return () => {
      clearTimeout(release);
      document.removeEventListener("pointerdown", down, true);
      document.removeEventListener("pointerup", up, true);
      document.removeEventListener("pointercancel", up, true);
      window.removeEventListener("blur", up);
    };
  }, []);
  const busy = count > 0 && !pointerActive && !pointerGesture.current;
  const overlay = useRef(null);
  const [brand, setBrand] = useState(null);
  const publishBrand = useCallback(value => {
    const owner = {};
    setBrand({ ...value, owner });
    return () => setBrand(current => current?.owner === owner ? null : current);
  }, []);
  useEffect(() => {
    if (!busy || !overlay.current) return undefined;
    const node = overlay.current, previousFocus = document.activeElement;
    const rootStyle = document.documentElement.style;
    const previousOverflow = rootStyle.getPropertyValue("overflow"), priority = rootStyle.getPropertyPriority("overflow");
    rootStyle.setProperty("overflow", "hidden");
    const preserved = new Map();
    const block = element => {
      if (element === node || preserved.has(element)) return;
      preserved.set(element, element.getAttribute("inert")); element.setAttribute("inert", "");
    };
    [...document.body.children].forEach(block);
    // Dialogs and popovers also use body portals; a new portal cannot bypass
    // the busy screen while the underlying action is still in flight.
    const observer = new MutationObserver(() => [...document.body.children].forEach(block));
    observer.observe(document.body, { childList: true });
    const focus = () => node.focus({ preventScroll: true });
    const keepFocus = event => { if (!node.contains(event.target)) { event.stopImmediatePropagation(); focus(); } };
    const key = event => { if (event.key === "Tab") { event.preventDefault(); focus(); } };
    document.addEventListener("focusin", keepFocus, true);
    document.addEventListener("keydown", key, true);
    focus();
    return () => {
      observer.disconnect();
      document.removeEventListener("focusin", keepFocus, true); document.removeEventListener("keydown", key, true);
      preserved.forEach((value, element) => { if (value === null) element.removeAttribute("inert"); else element.setAttribute("inert", value); });
      if (rootStyle.getPropertyValue("overflow") === "hidden") {
        if (previousOverflow) rootStyle.setProperty("overflow", previousOverflow, priority); else rootStyle.removeProperty("overflow");
      }
      if (previousFocus?.isConnected && !previousFocus.closest("[inert]")) previousFocus.focus({ preventScroll: true });
    };
  }, [busy]);
  return <LoaderBrandingContext.Provider value={publishBrand}><ActiveLoaderBrandingContext.Provider value={brand}>{children}{busy && createPortal(<div ref={overlay} role="dialog" aria-modal="true" aria-label="Please wait"
    aria-busy="true" tabIndex={-1} className="global-request-loader" data-testid="global-request-loader">
    <BrandedLoaderPanel brand={brand} label="Please wait…" hint="Your request is being processed." />
  </div>, document.body)}</ActiveLoaderBrandingContext.Provider></LoaderBrandingContext.Provider>;
}
